import Vue from 'vue'
import router from '@/router'
import { compressImage as smartCompressImage } from '@smartdaily/smart-compressor'
import { getApps, initializeApp } from 'firebase/app'
import { getDatabase, ref, get } from 'firebase/database'
import { firebaseConfig } from '@/config'

export const processAssetCategoryToCascader = (rawData) => {
  const tempData = []
  // 因向下相容，因此會有空值，其實不使用、因此濾掉
  const withoutEmpty = rawData.filter(el => el.Name !== '')
  const renamedProperties = withoutEmpty.map((el) => ({
    label: el.Name,
    value: el.Id,
    ParentId: el.ParentId
  }))
  // 認命寫遞迴結果煜平說目前只會用到父子兩層 🥲
  const findParentArr = (obj, currentArr) => {
    let result = null
    let targetObj = null
    if (
      currentArr.length > 0 &&
      currentArr.find((el) => el.value === obj.ParentId)
    ) {
      // 如果找到父層
      targetObj = currentArr.find((el) => el.value === obj.ParentId)
      return targetObj
    } else {
      // 如果找不到父層則繼續找
      for (let index = 0; index < currentArr.length; index++) {
        if (Array.isArray(currentArr[index].children)) {
          const childrenArr = currentArr[index].children
          result = findParentArr(obj, childrenArr)
          if (result !== null) {
            return result
          }
        }
      }
      return null
    }
  }
  renamedProperties.forEach((obj) => {
    const parentArr = findParentArr(obj, tempData)
    if (!parentArr) {
      tempData.push(obj)
    } else {
      if (!Array.isArray(parentArr.children)) parentArr.children = []
      parentArr.children.push(obj)
    }
  })
  return tempData
}

// Google 表單網址轉換，以帶入使用者資訊、減少使用者的填寫成本 copy from 智生活
export const googleFormTransform = {
  fetchURL (url) {
    let returnURL = url
    if (url.includes('https://docs.google.com/forms/')) { // 必須為 Google 表單才進行資料轉換
      returnURL = this.matchData(returnURL)
    }
    return returnURL
  },
  matchData (url) {
    try {
      // 行銷 >> 1. 點擊取得預先填入的連結 2. 填入我們給的 key 作為 value 3. 取得連結 4. 將連結後段的 key, value 前後對調 5. 貼至 CMS 進行發佈
      // 程式端 >> 收到連結後便會解析、依需求帶入我們已知的資料（key, value 再翻轉回來）
      // 目前支援帶出的資料有 communityName(登入社區名稱)，name(管理員姓名)，phoneNumber(實名制手機號碼)
      // 示意發布連結： https://docs.google.com/forms/d/e/1FAIpQLSdsbIxQvd5Y2momBVTRPums0aLIQ0j_42fJRs06q1V-ktAh_g/viewform?usp=pp_url&communityName=entry.1400018552&name=entry.1664083730&phoneNumber=entry.961697274
      // 示意轉出連結： https://docs.google.com/forms/d/e/1FAIpQLSdsbIxQvd5Y2momBVTRPums0aLIQ0j_42fJRs06q1V-ktAh_g/viewform?usp=pp_url&entry.1400018552={現居社區}&entry.1664083730={姓名}&entry.961697274={電話}
      const googleForm = new URL(url)
      const googleFormUrlParams = new URLSearchParams(googleForm.search)
      const loginUserInfo = JSON.parse(localStorage.getItem('loginUserInfo'))
      const matchPhoneNumber = loginUserInfo.Account.match(/09\d{8}/)
      const info = {
        communityName: '' || loginUserInfo.CommunityName, // 現居社區
        name: '' || loginUserInfo.AdminName, // 使用者名稱 / 姓名 / 稱呼
        phoneNumber: matchPhoneNumber || '' // 手機號碼
      }
      for (const [urlKey, urlValue] of googleFormUrlParams.entries()) {
        // 有哪些需求參數
        for (const [infoKey, infoValue] of Object.entries(info)) {
          // 是否有資訊可以設置參數
          if (urlKey === infoKey) {
            googleForm.searchParams.set(urlValue, infoValue)
            googleForm.searchParams.delete(urlKey) // 需刪除原本識別轉換的key
          }
        }
      }
      return googleForm.toString()
    } catch (error) {
      return url
    }
  }
}

// 從連結取得副檔名
export const getExtensionFromLink = (link) => {
  // 找到最後一個斜線的索引
  const lastSlashIndex = link.lastIndexOf('/')

  // 從最後一個斜線後面的部分取得檔案名稱
  const fileName = link.substring(lastSlashIndex + 1)

  // 找到最後一個點的索引
  const lastDotIndex = fileName.lastIndexOf('.')

  // 若有找到最後一個點且它不在檔案名稱的第一個字元位置，表示有副檔名
  if (lastDotIndex !== -1 && lastDotIndex !== 0) {
    return '.' + fileName.substring(lastDotIndex + 1)
  } else {
    return '' // 若沒有副檔名，回傳空字串
  }
}

/**
 * 透過後端回傳的資料在前端創建下載連結以供使用者下載檔案。
 * @param {object} res - 後端 API 回傳的資料，應該包含以下結構：
 *   // 記得打API時的config要帶 responseType: 'blob'
 *   {
 *     headers: {
 *       'content-disposition': string // 後端附加的標頭，包含檔案名稱的訊息
 *     },
 *     data: ArrayBuffer // 後端回傳的檔案資料
 *   }
 * @throws {Error} 如果後端未提供 'content-disposition' 標頭，則拋出錯誤。
 */
export const createDownloadFile = (res) => {
  if (!res.headers['content-disposition']) {
    throw new Error("createDownloadFile()需要後端有附上'content-disposition'的header才可以使用！")
  }

  const url = window.URL.createObjectURL(new Blob([res.data]))
  const link = document.createElement('a')
  link.href = url
  link.setAttribute(
    'download',
    decodeURIComponent(
      res.headers['content-disposition'].split(' ')[2].replace("filename*=UTF-8''", '')
    )
  )
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

/**
 * 判斷是否為「不用登入即可進入的路由」
 * @param {object} [to] - 可選，為 Vue Router 的路由物件，預設為目前路由，可帶入即將進入的路由
 * @returns {boolean} 是否為「不用登入即可進入的路由」
 * @example
 * true: login / visitorRealNameRigistration / supplierMaintenance
 * false: loginTest / login!!!!! / news
 */
export const checkRouteIsNoNeedToLogin = (to) => {
  // 不用登入即可進入的路由
  // keyword: isNoNeedToLogin: true

  // 登入頁
  // 訪客管理 > 右上角現場訪客登記表單 QRCode 內容之網址
  // 訪客管理 > 右上角現場訪客登記表單 QRCode 內容之網址 > 登記完成頁面
  // 智慧建築首頁 > 資產管理 > 維養計畫 Tab > 右上角維養 QRCode 內容之網址

  // 如果父層路由 meta 有設定 isNoNeedToLogin，則視為不用登入即可進入的路由
  // 這樣即可避免每個子層 meta 都要設定 isNoNeedToLogin
  // 可參考 supplierMaintenance
  const route = to || router.currentRoute
  const mergedMeta = Object.assign(
    {},
    ...route.matched.map(record => record.meta)
  )

  return !!mergedMeta.isNoNeedToLogin
}

/**
 * 檢查登入狀態是否有效
 * @param {object} [param0={}] - 可選，跳過驗證的清單 skipValidationList
 * skipValidationList 目前僅支援 loginStatus，需要時再擴寫
 * @returns {boolean} 是否有效登入
 */
export const checkIsLoginValid = ({
  skipValidationList = []
} = {}) => {
  try {
    // 必須有 comLoginToken、登入資訊、登入狀態、過期時間
    const comLoginToken = Vue.prototype.$getCookie('comLoginToken')
    const loginUserInfo = JSON.parse(localStorage.getItem('loginUserInfo'))
    const loginStatus = skipValidationList.includes('loginStatus') ? true : JSON.parse(localStorage.getItem('loginStatus'))
    const expireTime = new Date(loginUserInfo.ExpireTime)
    // 判斷是否過期
    const isValidLoginTime = new Date() < expireTime
    return comLoginToken && loginUserInfo && loginStatus && isValidLoginTime
  } catch (error) {
    return false
  }
}

/**
 * 滾動到指定的元素，考慮到Header的高度
 * 若元素有多個，則跳到第一個 (通常為最上方的)
 * @param {string} selector - 必填，CSS 選擇器，用於選擇要滾動到的元素。
 * @param {number} [customPaddingTop] - 可選，固定導航欄的高度。如果未提供，則使用#header的高度
 */
export const scrollToElement = (selector, customPaddingTop) => {
  let paddingTop = 0 // 滾動時上方預留的空間 (通常是navbar)
  const extraPadding = 30 // 為避免滾動後太剛好貼著，多加一點空間

  // 如果提供了導航欄高度參數，則覆蓋預設值
  if (typeof customPaddingTop === 'number') {
    paddingTop = customPaddingTop
  } else if (document.querySelector('#header')) {
    paddingTop = document.querySelector('#header').offsetHeight + extraPadding
  } else {
    console.warn('你所在的頁面找不到#header，也沒有傳入customPaddingTop，滾動位置可能不如預期')
    paddingTop = 154 + extraPadding
  }

  // 根據傳入的選擇器選取目標元素
  const element = document.querySelector(selector)
  // 如果元素不存在，則不進行任何操作
  if (!element) {
    throw new Error(`未找到匹配的元素: ${selector}`)
  }

  // 獲取元素相對於可視區域的位置訊息
  const elementRect = element.getBoundingClientRect()

  // 計算元素相對於整個頁面的實際位置
  const elementPosition = elementRect.top + window.pageYOffset

  // 滾動到元素的位置，並加上padding
  window.scrollTo({
    top: elementPosition - paddingTop, // 減去導航欄高度
    behavior: 'smooth' // 平滑的滾動
  })
}

/**
 * 格式化日期時間字串
 * @param {string | Date} date - 要格式化的日期
 * @param {boolean} [showSeconds=false] - 是否顯示秒數，預設為 false
 * @returns {string} 格式化後的日期時間字串，格式為 'YYYY-MM-DD HH:mm' 或 'YYYY-MM-DD HH:mm:ss'
 */
export function formatDateTime (date, showSeconds = false) {
  const format = showSeconds ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD HH:mm'
  const parsedDate = dayjs(date)
  return parsedDate.format(format)
}

/**
 * 將 Blob 轉換為 File 物件
 * @param {Object} param0 - 參數物件
 * @param {Blob} param0.blob - 要轉換的 Blob 物件
 * @param {string} param0.fileName - 檔案名稱
 * @returns {File} 轉換後的 File 物件
 */
const blobToFile = ({
  blob,
  fileName
} = {}) => new File([blob], fileName, {
  type: blob.type,
  lastModified: Date.now()
})

/**
 * 從 Firebase 取得圖片壓縮品質設定
 * @returns {Promise<number>} 壓縮品質 (0-4)，預設為 2
 */
export const getCompressQuality = async () => {
  try {
    const _kingetcareFb =
    getApps().filter(x => x.name === 'kingnetcare').length === 1
      ? getApps().filter(x => x.name === 'kingnetcare')[0]
      : initializeApp(firebaseConfig, 'kingnetcare')
    const database = getDatabase(_kingetcareFb)
    const reference = ref(database, 'Management/defaultImageCompressQuality')
    const snapshot = await get(reference)
    return typeof snapshot.val() === 'number' ? snapshot.val() : 2 // 預設為 2 才不會使圖中文字糊成一片
  } catch {
    return 2
  }
}

/**
 * 圖片壓縮
 * @param sourceImage 原始圖片
 * @param quality 壓縮品質 (0-4)
 * @param isWebp 是否轉換為 WebP 格式
 * @returns 壓縮後的圖檔
 */
export const compressImage = async (
  sourceImage,
  quality = 2, // 預設為 2 才不會使圖中文字糊成一片
  isWebp = true
) => {
  const blob = await smartCompressImage(sourceImage, {
    quality,
    isWebp
  })
  return blobToFile({
    blob,
    fileName: blob.name
  })
}
